import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector }             from 'react-redux';
import { Link }                                 from 'react-router-dom';
import { useTheme }                             from '@mui/material/styles';
import { useMediaQuery }                        from '@mui/material';
import { Get_All_sales_For_Team_Leader_Selected_and_Stages_and_Leads } from '../Redux/Actions/SalesAction';
import { SalesForTeamLeaderCard }               from '../Component/Sales/SalesForTeamLeaderCard';
import { useSidebarWidth }                      from '../Utility/useSidebarWidth';

/* ─── Tokens ─────────────────────────────────────────────────────────────── */
const C = {
  bg:         '#F7F8FC',
  white:      '#FFFFFF',
  border:     '#E8EAF0',
  accent:     '#0D47A1',
  accentDark: '#0a3880',
  accentSoft: 'rgba(13,71,161,0.08)',
  text:       '#1C2340',
  textMuted:  '#7B829A',
  warning:    '#F79009',
  shadow:     '0 1px 3px rgba(16,24,40,0.08)',
  shadowMd:   '0 4px 16px rgba(16,24,40,0.10)',
};

const SIDEBAR_COLLAPSED = 64;

/* ─── Icons ──────────────────────────────────────────────────────────────── */
const IcoSearch  = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>;
const IcoUsers   = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
const IcoChevron = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>;
const IcoSpin    = () => <div style={{ width: 36, height: 36, border: `3px solid rgba(13,71,161,0.15)`, borderTopColor: C.accent, borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />;

/* ════════════════════════════════════════════════════════════════════════════
   MAIN
════════════════════════════════════════════════════════════════════════════ */
export const AllSalesForTeamLeaderPage = () => {
  const dispatch  = useDispatch();
  const isDark    = useTheme().palette.mode === 'dark';
  const isMobile  = useMediaQuery('(max-width:768px)');
  const sidebarW  = useSidebarWidth();

  const [userlogin, setUserlogin] = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState('');

  useEffect(() => {
    const u = localStorage.getItem('user');
    if (u) setUserlogin(JSON.parse(u));
  }, []);

  const userEmail = useMemo(() => userlogin?.data?.email || null, [userlogin]);

  useEffect(() => {
    if (!userEmail) return;
    setLoading(true);
    dispatch(Get_All_sales_For_Team_Leader_Selected_and_Stages_and_Leads(userEmail))
      .finally(() => setLoading(false));
  }, [userEmail, dispatch]);

  const resgetalldata    = useSelector(s => s.AllSales.Sales_For_Selected_teamLeader);
  const AllDataAnalytics = Array.isArray(resgetalldata?.data) ? resgetalldata.data : [];

  /* search filter */
  const filtered = AllDataAnalytics.filter(item =>
    !search || item.salesName?.toLowerCase().includes(search.toLowerCase())
  );

  const css = `
    @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
    @keyframes spin   { to { transform:rotate(360deg); } }
    @keyframes sfade  { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:none} }
    .tls-search:focus { outline:none; border-color:${C.accent} !important; }
    .tls-cta:hover:not(:disabled) { background:${C.accentDark} !important; transform:translateY(-1px); box-shadow:0 6px 20px rgba(13,71,161,0.35) !important; }
    .tls-cta:disabled { opacity:0.45; cursor:not-allowed; }
  `;

  return (
    <>
      <style>{css}</style>
      <div style={{
        marginLeft: isMobile ? SIDEBAR_COLLAPSED : sidebarW,
        marginTop: 64,
        minHeight: 'calc(100vh - 64px)',
        background: isDark ? '#111122' : C.bg,
        fontFamily: "'DM Sans', sans-serif",
        transition: 'margin-left 0.25s cubic-bezier(0.4,0,0.2,1)',
      }}>
        <div style={{ padding: isMobile ? '16px' : '28px 28px 60px' }}>

          {/* ── Breadcrumb ── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, marginBottom: 22, flexWrap: 'wrap' }}>
            <Link to="/AllLeadsForTeamLeader" style={{ textDecoration: 'none', color: isDark ? '#9ca3af' : '#6b7280', fontWeight: 500 }}>Home</Link>
            <span style={{ color: isDark ? '#4b5563' : '#d1d5db', display: 'flex' }}><IcoChevron /></span>
            <span style={{ color: isDark ? '#e5e7eb' : C.text, fontWeight: 600 }}>
              Sales Team — {userlogin?.data?.name}
            </span>
          </div>

          {/* ── Header ── */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 24 }}>
            <div>
              <p style={{ margin: '0 0 3px', fontSize: 10, fontWeight: 700, color: C.textMuted, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Team Leader</p>
              <h1 style={{ margin: '0 0 6px', fontSize: isMobile ? 26 : 34, fontWeight: 800, color: isDark ? '#e0e0ff' : C.text, letterSpacing: '-0.4px', lineHeight: 1.15 }}>
                My Sales<br />Team
              </h1>
              <p style={{ margin: 0, fontSize: 13, color: C.textMuted }}>
                {AllDataAnalytics.length} sales member{AllDataAnalytics.length !== 1 ? 's' : ''} in your team
              </p>
            </div>

            {/* Stat chip */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: isDark ? '#1a1a2e' : C.white, border: `1px solid ${isDark ? '#2a2a45' : C.border}`, borderRadius: 12, padding: '14px 20px', boxShadow: C.shadow, flexShrink: 0 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: C.accentSoft, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.accent }}>
                <IcoUsers />
              </div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.textMuted, letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 2 }}>Total Sales</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: isDark ? '#e0e0ff' : C.text, lineHeight: 1 }}>{AllDataAnalytics.length}</div>
              </div>
            </div>
          </div>

          {/* ── CTA — Advanced Search ── */}
          <Link to="/SalesForTeamLeaderWithDiffrentDates" style={{ textDecoration: 'none', display: 'block', marginBottom: 24 }}>
            <button
              className="tls-cta"
              style={{
                width: '100%', padding: '13px 0', borderRadius: 12, border: 'none',
                background: `linear-gradient(135deg, ${C.accent}, ${C.accentDark})`,
                color: '#fff', fontSize: 14, fontWeight: 700,
                cursor: 'pointer', fontFamily: "'DM Sans',sans-serif",
                boxShadow: '0 4px 14px rgba(13,71,161,0.3)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                transition: 'all 0.15s',
              }}
            >
              <IcoSearch />
              Search Sales Performance by Date Range
            </button>
          </Link>

          {/* ── Search ── */}
          <div style={{ background: isDark ? '#1a1a2e' : C.white, border: `1px solid ${isDark ? '#2a2a45' : C.border}`, borderRadius: 12, padding: '10px 16px', boxShadow: C.shadow, marginBottom: 22, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ color: C.textMuted, display: 'flex' }}><IcoSearch /></span>
            <input
              className="tls-search"
              placeholder="Search sales person by name…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ border: 'none', background: 'none', outline: 'none', fontSize: 14, color: isDark ? '#e0e0ff' : C.text, fontFamily: "'DM Sans',sans-serif", width: '100%' }}
            />
            {search && (
              <button onClick={() => setSearch('')} style={{ border: 'none', background: 'none', cursor: 'pointer', color: C.textMuted, fontSize: 16, display: 'flex' }}>✕</button>
            )}
          </div>

          {/* ── Content ── */}
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 20px', gap: 14 }}>
              <IcoSpin />
              <span style={{ fontSize: 14, color: C.textMuted }}>Loading sales team data…</span>
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ background: isDark ? '#1a1a2e' : C.white, border: `1px solid ${isDark ? '#2a2a45' : C.border}`, borderRadius: 16, padding: '60px 20px', textAlign: 'center', color: C.textMuted, boxShadow: C.shadow }}>
              <p style={{ fontSize: 14, margin: '0 0 8px' }}>{search ? `No sales member found matching "${search}"` : 'No sales data available yet.'}</p>
              {search && <button onClick={() => setSearch('')} style={{ border: 'none', background: 'none', color: C.accent, fontFamily: "'DM Sans',sans-serif", fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>Clear search</button>}
            </div>
          ) : (
            <div
              style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 18 }}
            >
              {filtered.map((item, i) => (
                <div key={i} style={{ animation: `sfade 0.35s ${i * 0.04}s both` }}>
                  <SalesForTeamLeaderCard salesdata={item} />
                </div>
              ))}
            </div>
          )}

        </div>
      </div>
    </>
  );
};
